import React, { useState, useEffect } from 'react';
import { LayoutAnimation, Text, View } from 'react-native';
import { connect } from 'react-redux';

import ColorWrapper from '@styles/colors';
import { PopupWrapper } from '@components/popup';

import SelectType from './components/selectType';
import ConnectorDetails from './components/connectorDetails';
import DriversFolders from './components/driversFolders';
import TimeZone from './components/timeZone';

import { erpSourceSystemTypes, onboardProductionSourceSystem } from '@reducers/connector';
import { connectorProductionConfigurationDetails, getTimezone } from '@reducers/membership';
import { setStorage } from './reducer';

import { Alert, ERP_SYSTEM_CONNECTION_TYPE, isPhone, SourceSystems } from '@helpers';

import { Connector, ProductionConnectorSetupProps, MODES, Mode, UpdateDataHistoryObjFn } from './interfaces';
import { notchTop, scale } from '@components/frame';
import i18n from 'i18n-js';

const mapConnectorDoData = (connector: Connector | null) => {
  if (!connector) return {};

  const data: Record<string, any> = {};

  data.folders =
    connector?.documentsConnectionProperties?.documentSources?.map((i) => ({
      folder: i.mappedNetworkDrive,
      UNC: i.networkPath,
      id: Math.round(Math.random() * 10000),
    })) || [];

  data.documentsRootFolder = connector.documentsConnectionProperties?.documentsRootFolder;

  const arr = connector.documentsConnectionProperties?.credentials?.userName?.split('\\');
  data.fileDomain = arr?.[0];
  data.fileUser = arr?.[1];

  data.isEnabled = connector.isEnabled;
  data.timeZoneWindows = connector.timeZoneWindows;

  switch (connector.code) {
    case SourceSystems.MKG:
      if (connector.sourceSystemConnectionType === ERP_SYSTEM_CONNECTION_TYPE.MKG_ODBC) {
        data.host = connector.databaseOdbcConnectionProperties?.host;
        data.database = connector.databaseOdbcConnectionProperties?.database;
        data.dataSourceName = connector.databaseOdbcConnectionProperties?.dataSourceName;
        data.user = connector.databaseOdbcConnectionProperties?.credentials?.userName;
        break;
      }

      if (connector.sourceSystemConnectionType === ERP_SYSTEM_CONNECTION_TYPE.MKG_API) {
        data.apiKey = connector.apiConnectionProperties?.apiKey;
        data.apiRootEndpoint = connector.apiConnectionProperties?.apiRootEndpoint;
        data.user = connector.apiConnectionProperties?.credentials?.userName;
        break;
      }

    case SourceSystems.Bemet:
      data.host = connector.databaseNpgsqlConnectionProperties?.host;
      data.database = connector.databaseNpgsqlConnectionProperties?.database;
      data.user = connector.databaseNpgsqlConnectionProperties?.credentials?.userName;
      break;
    case SourceSystems.proMExS:
      data.serverEndpoint = connector.databaseSqlClientConnectionProperties?.serverEndpoint;
      data.databaseName = connector.databaseSqlClientConnectionProperties?.databaseName;
      data.user = connector.databaseSqlClientConnectionProperties?.credentials?.userName;
      break;
  }

  if (connector.filterProperties) {
    Object.keys(connector.filterProperties).forEach((key) => {
      data[key] = connector.filterProperties![key];
    });
  }

  return data;
};

const ProductionConnectorSetup: React.FC<ProductionConnectorSetupProps> = (props) => {
  const { hide, connectorDetails, erpSourceSystemTypes, setStorage, storage, C } = props;

  const connectorAlreadyExist = Boolean(connectorDetails);

  const [data, setData] = useState(storage.data ? storage.data : connectorAlreadyExist ? mapConnectorDoData(connectorDetails) : {});
  const [dataHistory, setDataHistory] = useState(storage.dataHistory ? storage.dataHistory : data);
  const [isDataChanged, setIsDataChanged] = useState(storage.isDataChanged ? storage.isDataChanged : false);
  const [isEditMode, setIsEditMode] = useState(storage.isEditMode ? storage.isEditMode : !connectorAlreadyExist);
  const [mode, setModeValue] = useState<Mode>(storage.mode ? storage.mode : connectorAlreadyExist ? MODES.CONNECTOR_DETAILS : MODES.SELECT_TYPE);
  const [modeHistory, setModeHistory] = useState<Mode[]>(storage.modeHistory ? storage.modeHistory : []);

  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    setDataHistory(JSON.parse(JSON.stringify(data)));
  }, [data]);

  useEffect(() => {
    setIsEditMode(!connectorAlreadyExist);

    return () => {
      setStorage({
        isEditMode: undefined,
        mode: undefined,
        modeHistory: undefined,
        data: undefined,
        dataHistory: undefined,
        isDataChanged: undefined,
      });
    };
  }, []);

  useEffect(() => {
    setStorage({
      isEditMode,
      mode,
      modeHistory,
      data,
      dataHistory,
      isDataChanged,
    });
  }, [isEditMode, mode, modeHistory, data, dataHistory, isDataChanged]);

  const ignoreHistoryPool = [MODES.SELECT_TYPE] as Mode[];
  const setMode = (v: Mode) => {
    const last = modeHistory[modeHistory.length - 1];
    if (!ignoreHistoryPool.includes(mode) && mode !== last && v !== last && v !== mode) {
      setModeHistory([...modeHistory, mode]);
    }
    if (last === v) {
      const history = [...modeHistory];
      history.pop();
      setModeHistory(history);
    }
    LayoutAnimation.easeInEaseOut();
    setModeValue(v);
  };

  const goBack = (storage?: Mode[]) => {
    const history = [...(storage || modeHistory)];
    const last = history.pop();
    if (last === mode) {
      return goBack(history);
    }
    if (last) {
      LayoutAnimation.easeInEaseOut();
      setModeValue(last);
      setModeHistory(history);
    } else {
      hide();
    }
  };

  const updateData = (key: string, value: any) => {
    setIsDataChanged(true);
    setData({
      ...data,
      [key]: value,
    });
  };

  const updateDataHistory = (key: string, value: any) => {
    setIsDataChanged(true);
    setDataHistory({
      ...dataHistory,
      [key]: value,
    });
  };

  const updateDataObj = (obj: Record<string, any>) => {
    setData({
      ...data,
      ...obj,
    });
  };

  const updateDataHistoryObj: UpdateDataHistoryObjFn = ({ changes, dataIsChanged }) => {
    if (dataIsChanged) {
      setIsDataChanged(true);
    }
    setDataHistory({
      ...dataHistory,
      ...changes,
    });
  };

  useEffect(() => {
    (async () => {
      if (isError) {
        setIsError(false);
      }
      if (connectorAlreadyExist) {
        const item = await erpSourceSystemTypes({
          filter: `?$filter=code eq '${connectorDetails?.code}' and sourceSystemConnectionType eq '${connectorDetails?.sourceSystemConnectionType}'`,
        });
        if (item.length !== 1) {
          Alert({ title: i18n.t('Something didn’t work') });
          setIsError(true);
        } else {
          if (item?.[0]) {
            updateData('selectedType', item[0]);
          }
        }
      }
    })();
  }, [connectorAlreadyExist]);

  const renderComponent = () => {
    const p = {
      ...props,
      setMode,
      MODES,
      setData,
      updateData,
      updateDataObj,
      updateDataHistoryObj,
      updateDataHistory,
      data,
      goBack,
      connectorAlreadyExist,
      isEditMode,
      setIsEditMode,
      dataHistory,
      setDataHistory,
      isDataChanged,
      setIsDataChanged,
    };

    switch (mode) {
      case MODES.SELECT_TYPE:
        return <SelectType {...(p as any)} />;
      case MODES.CONNECTOR_DETAILS:
        return <ConnectorDetails {...(p as any)} isError={isError} />;
      case MODES.DRIVERS_FOLDERS:
        return <DriversFolders {...(p as any)} />;
      case MODES.TIME_ZONE:
        return <TimeZone {...(p as any)} />;
      default:
        return null;
    }
  };

  return <View style={[{ paddingTop: scale(notchTop + 12) }, isPhone && { flex: 1 }]}>{renderComponent()}</View>;
};

const mapStateToProps = (state: any) => ({
  storage: state.productionConnectorSetup.storage,
  connectorDetails: state.membership?.connectorDetails || null,
  connectorDetailsLoaded: state.membership?.connectorDetailsLoaded || null,
  user: state.user,
});

const connector = connect(mapStateToProps, {
  erpSourceSystemTypes,
  onboardProductionSourceSystem,
  connectorProductionConfigurationDetails,
  getTimezone,
  setStorage,
});

const Wrapper: React.FC<ProductionConnectorSetupProps> = (props) => {
  return (
    <PopupWrapper>
      <ProductionConnectorSetup {...props} />
    </PopupWrapper>
  );
};

export default connector(ColorWrapper(Wrapper));
